import React, { useState, useRef, useLayoutEffect, useCallback, useEffect } from 'react';
import { SidebarCustomizerModal, useSidebarVisibility } from './SidebarCustomizer';
import PinGate, { isCustomizerUnlocked } from './SidebarCustomizerPinGate';
import {
  House, Bell, Coins, BarChart3, FileBarChart, Target, Palette, Wine, AlertTriangle,
  Truck, Package, ClipboardList, Receipt, PartyPopper, Crown, Beer, BookOpen, BookMarked,
  Gift, Megaphone, Shirt, Briefcase, Users, CalendarCheck, Croissant, Handshake, FolderOpen,
  CalendarClock, Star, UserPlus, Settings, TrendingUp, Search, SlidersHorizontal, X,
  DollarSign, Notebook,
} from 'lucide-react';
import './Sidebar.css';

// Map: module key → lucide icon component
const NAV_ICONS = {
  home: House,
  dashboard: House,
  reminders: Bell,
  add: Coins,
  summary: BarChart3,
  reports: FileBarChart,
  billard: Target,
  graphic_projects: Palette,
  glass: Wine,
  kitchen_losses: AlertTriangle,
  jerry_drive: Truck,
  warehouse: Package,
  restaurant_matters: ClipboardList,
  invoices: Receipt,
  events_catering: PartyPopper,
  delivery_survey: Target,
  club_admin: Crown,
  beer_cleaning: Beer,
  training: BookOpen,
  materials: BookMarked,
  briefing: Notebook,
  sales: DollarSign,
  promotions: Gift,
  marketing: Megaphone,
  daily_settlement: BookOpen,
  deliveries: Package,
  clothing: Shirt,
  export: Package,
  manager: Briefcase,
  development: Users,
  recruitment: CalendarCheck,
  manager_analytics: TrendingUp,
  manager_performance: Target,
  delikatesy: Croissant,
  team_engagement: Handshake,
  cash: DollarSign,
  files: FolderOpen,
  inventory: Package,
  monthly_closing: CalendarClock,
  reviews: Star,
  recruitment_apps: UserPlus,
  settings: Settings,
  tutorial: BookOpen,
};

const NavIcon = ({ name }) => {
  const Icon = NAV_ICONS[name] || House;
  return <Icon className="nav-icon-svg" size={18} strokeWidth={1.7} aria-hidden />;
};

// Map: tabId → funkcja prefetch (kliknij = już w cache). Wykonujemy raz per tabId/sesja.
const PREFETCHED = new Set();
const prefetchTab = (tabId) => {
  if (PREFETCHED.has(tabId)) return;
  PREFETCHED.add(tabId);
  // Wstępne ładowanie chunków (webpack bundle splitting groups)
  const CORE = ['sales', 'restaurant_matters', 'invoices', 'reminders', 'tips', 'summary', 'dashboard', 'daily_briefing'];
  const MANAGER = ['manager', 'manager_analytics', 'manager_performance', 'employees', 'recruitment', 'recruitment_applications', 'reviews', 'reports', 'team_engagement'];
  const OPERATIONS = ['billard', 'jerry_drive', 'warehouse', 'warehouse_protocols', 'kitchen_losses', 'kitchen_losses_reports', 'beer_cleaning', 'deliveries', 'glass', 'clothing', 'inventory', 'monthly_closing', 'cash'];
  const CONTENT = ['training', 'materials', 'changelog', 'graphic_projects', 'promotions', 'delikatesy', 'communicator'];
  const ADMIN = ['settings', 'export', 'development', 'tutorial'];
  if (CORE.includes(tabId)) import(/* webpackChunkName: "core" */ '../tabs/SalesTab').catch(() => {});
  else if (MANAGER.includes(tabId)) import(/* webpackChunkName: "manager" */ '../manager/ManagerTab').catch(() => {});
  else if (OPERATIONS.includes(tabId)) import(/* webpackChunkName: "operations" */ '../tabs/JerryDriveTab').catch(() => {});
  else if (CONTENT.includes(tabId)) import(/* webpackChunkName: "content" */ '../tabs/MaterialsTab').catch(() => {});
  else if (ADMIN.includes(tabId)) import(/* webpackChunkName: "admin" */ '../tabs/SettingsTab').catch(() => {});
};

const Navbar = ({ 
  activeTab, 
  setActiveTab: setActiveTabRaw, 
  hasPermission,
  location,
  user,
  isSidebarOpen,
  setIsSidebarOpen,
  isSidebarCollapsed = false,
  communicatorUnread = 0,
  remindersCount = 0,
  unreadFlags = {},
}) => {
  const [search, setSearch] = useState('');
  const asideRef = useRef(null);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [showCustomizer, setShowCustomizer] = useState(false);
  const [showPinGate, setShowPinGate] = useState(false);

  // Otwórz customizer albo poproś o PIN
  const openCustomizerOrPin = useCallback(() => {
    if (isCustomizerUnlocked()) {
      setShowCustomizer(true);
    } else {
      setShowPinGate(true);
    }
  }, []);

  // Na mobile: po wyborze modułu automatycznie zamknij sidebar
  const setActiveTab = useCallback((tabId) => {
    setActiveTabRaw(tabId);
    if (typeof window !== 'undefined' && window.innerWidth <= 768 && typeof setIsSidebarOpen === 'function') {
      setIsSidebarOpen(false);
    }
  }, [setActiveTabRaw, setIsSidebarOpen]);

  // Hook musi być wywołany za każdym renderem aby re-applyować widoczność
  useSidebarVisibility(user?.username, asideRef, user?.id);

  // Nasłuchuj event z modala żeby wymusić re-render (i re-apply)
  const [, forceRerender] = useState(0);
  useEffect(() => {
    const handler = () => forceRerender((x) => x + 1);
    window.addEventListener('sidebar-visibility-changed', handler);
    return () => window.removeEventListener('sidebar-visibility-changed', handler);
  }, []);

  // Filtruj przyciski nav po tekście — iterujemy po faktycznie wyrenderowanych .nav-btn
  useLayoutEffect(() => {
    if (!asideRef.current) return;
    const q = search.trim().toLowerCase();
    const buttons = asideRef.current.querySelectorAll('.nav-btn');
    let hidden = 0;
    buttons.forEach((btn) => {
      // Uszanuj personalizację użytkownika — jeśli ukryty przez customizer, nie zmieniaj
      if (btn.dataset.sidebarHidden === 'true') {
        btn.style.display = 'none';
        return;
      }
      if (!q) {
        btn.style.display = '';
        return;
      }
      const text = (btn.textContent || '').toLowerCase();
      if (text.includes(q)) {
        btn.style.display = '';
      } else {
        btn.style.display = 'none';
        hidden += 1;
      }
    });
    setHiddenCount(hidden);
  }); // brak deps — triggeruje po każdym renderze (nav buttons mogą być warunkowe)

  const badgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '20px',
    height: '20px',
    padding: '0 6px',
    marginLeft: '8px',
    borderRadius: '999px',
    background: '#ef4444',
    color: '#fff',
    fontSize: '11px',
    fontWeight: 700,
    lineHeight: 1,
    boxShadow: '0 0 0 2px rgba(239,68,68,0.25)',
    animation: 'pulse-badge 2s ease-in-out infinite',
  };
  return (
    <>
      {/* Sidebar Overlay for Mobile - hamburger is now in Header */}
      {isSidebarOpen && (
        <div 
          className="sidebar-overlay"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Left Sidebar Navigation */}
      <aside ref={asideRef} className={`sidebar ${isSidebarOpen ? 'sidebar-open' : ''} ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* WYSZUKIWARKA MODUŁÓW — fixed na górze, nie scrolluje się */}
      <div className="sidebar-search-wrapper">
        <div className="sidebar-search-row">
          <div className="sidebar-search-inputbox">
            <Search size={14} className="sidebar-search-icon" strokeWidth={1.9} />
            <input
              type="search"
              name="sidebar_module_search"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Szukaj modułu…"
              data-testid="sidebar-search-input"
              className="sidebar-search-field"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                data-testid="sidebar-search-clear"
                aria-label="Wyczyść"
                className="sidebar-search-clear"
              ><X size={12} /></button>
            )}
          </div>
          <button
            type="button"
            onClick={openCustomizerOrPin}
            data-testid="sidebar-customize-btn"
            aria-label="Personalizuj menu"
            title="Personalizuj menu — wybierz które moduły widzieć (chroniony PIN-em)"
            className="sidebar-customize-btn"
          >
            <SlidersHorizontal size={15} strokeWidth={1.9} />
          </button>
        </div>
        {search && (
          <div data-testid="sidebar-search-hits" className="sidebar-search-hits">
            {hiddenCount === 0 ? 'Wszystkie pasują' : `Ukryto ${hiddenCount} nie pasujących`}
          </div>
        )}
      </div>
      <div className="sidebar-scroll-area">
      {/* HOME — strona powitalna (default) */}
      <button
        className={`nav-btn ${activeTab === "home" ? "active" : ""}`}
        onClick={() => setActiveTab("home")}
        data-testid="nav-home"
      >
        <NavIcon name="home" /> Strona główna
      </button>

      {/* DASHBOARD / START — tymczasowo wyłączone */}
      {false && (
      <button 
        className={`nav-btn ${activeTab === "dashboard" ? "active" : ""}`} 
        onClick={() => setActiveTab("dashboard")}
        data-testid="nav-dashboard"
      >
        <NavIcon name="dashboard" /> Start
      </button>
      )}

      {/* COMMUNICATOR — przeniesiony do globalnego widgetu FAB (prawy dolny róg).
          Zostawiamy URL /komunikator dostępny dla legacy linków,
          ale przycisk w sidebarze ukryty. */}

      {/* REMINDERS */}
      <button
        className={`nav-btn ${activeTab === "reminders" ? "active" : ""}`}
        onClick={() => setActiveTab("reminders")}
        data-testid="nav-reminders"
      >
        <NavIcon name="reminders" /> Przypomnienia
        {remindersCount > 0 && activeTab !== 'reminders' && (
          <span style={badgeStyle} data-testid="nav-reminders-badge">
            {remindersCount > 99 ? '99+' : remindersCount}
          </span>
        )}
      </button>

      {/* ADD TIP */}
      {hasPermission('tips') && (
        <button 
          className={`nav-btn ${activeTab === "add" ? "active" : ""}`} 
          onClick={() => setActiveTab("add")}
        >
          <NavIcon name="add" /> Dodaj napiwek
        </button>
      )}

      {/* SUMMARY */}
      {hasPermission('summary') && (
        <button 
          className={`nav-btn ${activeTab === "summary" ? "active" : ""}`} 
          onClick={() => setActiveTab("summary")}
        >
          <NavIcon name="summary" /> Podsumowanie
        </button>
      )}

      {/* REPORTS */}
      {hasPermission('reports') && (
        <button 
          className={`nav-btn ${activeTab === "reports" ? "active" : ""}`} 
          onClick={() => setActiveTab("reports")}
        >
          <NavIcon name="reports" /> Raporty
        </button>
      )}

      {/* BILLARD - Nowy Sącz only */}
      {location === "nowysacz" && hasPermission('billard') && (
        <button 
          className={`nav-btn ${activeTab === "billard" ? "active" : ""}`} 
          onClick={() => setActiveTab("billard")}
        >
          <NavIcon name="billard" /> Billard
        </button>
      )}

      {/* GRAPHIC PROJECTS - Nowy Sącz only */}
      {location === "nowysacz" && hasPermission('graphic_projects') && (
        <button 
          className={`nav-btn ${activeTab === "graphic_projects" ? "active" : ""}`} 
          onClick={() => setActiveTab("graphic_projects")}
        >
          <NavIcon name="graphic_projects" /> Grafika
        </button>
      )}

      {/* GLASS */}
      {hasPermission('glass') && (
        <button 
          className={`nav-btn ${activeTab === "glass" ? "active" : ""}`} 
          onClick={() => setActiveTab("glass")}
        >
          <NavIcon name="glass" /> Szkła
        </button>
      )}

      {/* KITCHEN LOSSES */}
      {(hasPermission('kitchen_losses') || hasPermission('straty_zgloszenia') || hasPermission('straty_raporty') || hasPermission('straty_protokoly')) && (
        <button 
          className={`nav-btn ${activeTab === "kitchen_losses" ? "active" : ""}`} 
          onClick={() => setActiveTab("kitchen_losses")}
        >
          <NavIcon name="kitchen_losses" /> Straty
        </button>
      )}

      {/* JERRY DRIVE - Nowy Sącz only */}
      {location === 'nowysacz' && hasPermission('jerry_drive') && (
        <button
          className={`nav-btn ${activeTab === "jerry_drive" ? "active" : ""}`}
          onClick={() => setActiveTab("jerry_drive")}
          data-testid="nav-jerry-drive"
        >
          <NavIcon name="jerry_drive" /> Jerry Drive
        </button>
      )}

      {/* MAGAZYN — tylko Gródek */}
      {location === 'grodek' && (hasPermission('warehouse_view') || hasPermission('warehouse_manage') || hasPermission('manager') || hasPermission('settings')) && (
        <button
          className={`nav-btn ${activeTab === "warehouse" ? "active" : ""}`}
          onClick={() => setActiveTab("warehouse")}
          data-testid="nav-warehouse"
        >
          <NavIcon name="warehouse" /> Magazyn
        </button>
      )}

      {/* SPRAWY RESTAURACJI — wymaga specyficznego uprawnienia restaurant_matters lub admina */}
      {(hasPermission('restaurant_matters') || user?.role === 'admin') && (
        <button
          className={`nav-btn ${activeTab === "restaurant_matters" ? "active" : ""}`}
          onClick={() => setActiveTab("restaurant_matters")}
          data-testid="nav-restaurant-matters"
        >
          <NavIcon name="restaurant_matters" /> Sprawy restauracji
          {unreadFlags.restaurant_matters && <UnreadDot />}
        </button>
      )}

      {/* FAKTURY I PŁATNOŚCI — uprawnienia invoices_view / invoices_manage */}
      {(hasPermission('invoices_view') || hasPermission('invoices_manage') || hasPermission('manager') || hasPermission('settings') || user?.role === 'admin') && (
        <button
          className={`nav-btn ${activeTab === "invoices" ? "active" : ""}`}
          onClick={() => setActiveTab("invoices")}
          data-testid="nav-invoices"
        >
          <NavIcon name="invoices" /> Faktury i płatności
        </button>
      )}

      {/* KONFIGURATOR PRZYJĘĆ / MENU GRUPOWE */}
      {(hasPermission('events_catering') || hasPermission('manager') || hasPermission('settings') || user?.role === 'admin') && (
        <button
          className={`nav-btn ${activeTab === "events_catering" ? "active" : ""}`}
          onClick={() => setActiveTab("events_catering")}
          data-testid="nav-events-catering"
        >
          <NavIcon name="events_catering" /> Konfigurator przyjęć
        </button>
      )}

      {/* DOSTAWY — ankieta + kupony (tylko Nowy Sącz) */}
      {location === 'nowysacz' && (hasPermission('delivery_survey') || hasPermission('delivery_complaints_only') || hasPermission('manager') || hasPermission('settings') || user?.role === 'admin') && (
        <button
          className={`nav-btn ${activeTab === "delivery_survey" ? "active" : ""}`}
          onClick={() => setActiveTab("delivery_survey")}
          data-testid="nav-delivery-survey"
        >
          <NavIcon name="delivery_survey" /> {hasPermission('delivery_complaints_only') && !hasPermission('delivery_survey') ? 'Reklamacje i kupony' : 'Ankiety zadowolenia'}
        </button>
      )}

      {/* MR JERRY CLUB — program lojalnościowy */}
      {(hasPermission('manager') || hasPermission('settings') || user?.role === 'admin') && (
        <button
          className={`nav-btn ${activeTab === "club_admin" ? "active" : ""}`}
          onClick={() => setActiveTab("club_admin")}
          data-testid="nav-club-admin"
        >
          <NavIcon name="club_admin" /> MR JERRY CLUB
        </button>
      )}

      {/* BEER CLEANING - for both locations */}
      {hasPermission('beer_cleaning') && (
        <button 
          className={`nav-btn ${activeTab === "beer_cleaning" ? "active" : ""}`} 
          onClick={() => setActiveTab("beer_cleaning")}
        >
          <NavIcon name="beer_cleaning" /> Czyszczenie piwa
        </button>
      )}

      {/* TRAINING */}
      {hasPermission('training') && (
        <button 
          className={`nav-btn ${activeTab === "training" ? "active" : ""}`} 
          onClick={() => setActiveTab("training")}
        >
          <NavIcon name="training" /> Szkolenie
        </button>
      )}

      {/* MATERIALS */}
      {hasPermission('materials_view') && (
        <button 
          className={`nav-btn ${activeTab === "materials" ? "active" : ""}`} 
          onClick={() => setActiveTab("materials")}
        >
          <NavIcon name="materials" /> Materiały
        </button>
      )}

      {/* DAILY BRIEFING (INFORMACJE DNIA) */}
      {hasPermission('briefing_view') && (
        <button 
          className={`nav-btn ${activeTab === "briefing" ? "active" : ""}`} 
          onClick={() => setActiveTab("briefing")}
        >
          <NavIcon name="briefing" /> Informacje Dnia
        </button>
      )}

      {/* SALES MODULE */}
      <button 
        className={`nav-btn ${activeTab === "sales" ? "active" : ""}`} 
        onClick={() => setActiveTab("sales")}
      >
        <NavIcon name="sales" /> Sprzedaż
      </button>

      {/* PROMOTIONS */}
      {hasPermission('promotions') && (
        <button 
          className={`nav-btn ${activeTab === "promotions" ? "active" : ""}`} 
          onClick={() => setActiveTab("promotions")}
        >
          <NavIcon name="promotions" /> Promocje
        </button>
      )}

      {/* MARKETING — moduł marketingowy */}
      {hasPermission('marketing') && (
        <button
          className={`nav-btn ${activeTab === "marketing" ? "active" : ""}`}
          onClick={() => setActiveTab("marketing")}
          data-testid="nav-marketing"
        >
          <NavIcon name="marketing" /> Marketing
        </button>
      )}

      {/* KONTROLA ROZLICZEŃ DZIENNYCH — kierownik zamyka dzień, manager kontroluje */}
      {hasPermission('daily_settlement') && (
        <button
          className={`nav-btn ${activeTab === "daily_settlement" ? "active" : ""}`}
          onClick={() => setActiveTab("daily_settlement")}
          data-testid="nav-daily-settlement"
        >
          <NavIcon name="daily_settlement" /> Rozliczenia Dzienne
        </button>
      )}

      {/* DELIVERIES - Kraków only */}
      {location === 'krakow' && hasPermission('deliveries_view') && (
        <button 
          className={`nav-btn ${activeTab === "deliveries" ? "active" : ""}`} 
          onClick={() => setActiveTab("deliveries")}
        >
          <NavIcon name="deliveries" /> Zakupy
        </button>
      )}

      {/* CLOTHING */}
      {(hasPermission('clothing') || hasPermission('clothing_add')) && (
        <button 
          className={`nav-btn ${activeTab === "clothing" ? "active" : ""}`} 
          onClick={() => setActiveTab("clothing")}
        >
          <NavIcon name="clothing" /> Odzież
        </button>
      )}

      {/* EXPORT */}
      {hasPermission('export') && (
        <button 
          className={`nav-btn ${activeTab === "export" ? "active" : ""}`} 
          onClick={() => setActiveTab("export")}
        >
          <NavIcon name="export" /> Eksport
        </button>
      )}

      {/* SAMOUCZEK — wyłączony tymczasowo */}
      {false && (
        <button 
          className={`nav-btn ${activeTab === "tutorial" ? "active" : ""}`} 
          onClick={() => setActiveTab("tutorial")}
          data-testid="nav-tutorial"
        >
          <NavIcon name="tutorial" /> Samouczek
        </button>
      )}

      {/* MANAGER - Employee Management (Only for managers) */}
      {(hasPermission('manager') || hasPermission('settings')) && (
        <button 
          className={`nav-btn ${activeTab === "manager" ? "active" : ""}`} 
          onClick={() => setActiveTab("manager")}
        >
          <NavIcon name="manager" /> Manager
        </button>
      )}

      {/* DEVELOPMENT TEAM - Ideas & Problems (Nowy Sącz OR Developer) */}
      {(location === "nowysacz" || user?.username === 'Developer') && hasPermission('settings') && (
        <button 
          className={`nav-btn ${activeTab === "development" ? "active" : ""}`} 
          onClick={() => setActiveTab("development")}
          data-testid="nav-development"
        >
          <NavIcon name="development" /> Zespół rozwoju
          {unreadFlags.development && <UnreadDot />}
        </button>
      )}

      {/* RECRUITMENT - Trial days for candidates */}
      {(hasPermission('recruitment') || hasPermission('recruitment_view')) && (
        <button 
          className={`nav-btn ${activeTab === "recruitment" ? "active" : ""}`} 
          onClick={() => setActiveTab("recruitment")}
          data-testid="nav-recruitment"
        >
          <NavIcon name="recruitment" /> Dni próbne
        </button>
      )}

      {/* MANAGER ANALYTICS */}
      {(hasPermission('manager_analytics') || hasPermission('manager_analytics_view') || hasPermission('manager_analytics_edit') || hasPermission('manager') || hasPermission('settings')) && (
        <button
          className={`nav-btn ${activeTab === "manager_analytics" ? "active" : ""}`}
          onClick={() => setActiveTab("manager_analytics")}
          data-testid="nav-manager-analytics"
        >
          <NavIcon name="manager_analytics" /> Analiza managerska
        </button>
      )}

      {/* MANAGER PERFORMANCE — codzienne wpisy kierownika + AI raport */}
      {(hasPermission('manager_performance_view') || hasPermission('manager_performance_edit') || hasPermission('manager_performance_owner') || hasPermission('manager') || hasPermission('settings')) && (
        <button
          className={`nav-btn ${activeTab === "manager_performance" ? "active" : ""}`}
          onClick={() => setActiveTab("manager_performance")}
          data-testid="nav-manager-performance"
        >
          <NavIcon name="manager_performance" /> Panel kierownika restauracji
        </button>
      )}

      {/* DELIKATESY — TYLKO NOWY SĄCZ */}
      {location === 'nowysacz' && (hasPermission('delikatesy') || hasPermission('delikatesy_view') || hasPermission('delikatesy_edit') || hasPermission('manager') || hasPermission('settings')) && (
        <button
          className={`nav-btn ${activeTab === "delikatesy" ? "active" : ""}`}
          onClick={() => setActiveTab("delikatesy")}
          data-testid="nav-delikatesy"
        >
          <NavIcon name="delikatesy" /> Delikatesy
        </button>
      )}

      {/* TEAM ENGAGEMENT */}
      {(hasPermission('team_engagement') || hasPermission('team_engagement_manage') || hasPermission('manager') || hasPermission('settings')) && (
        <button
          className={`nav-btn ${activeTab === "team_engagement" ? "active" : ""}`}
          onClick={() => setActiveTab("team_engagement")}
          data-testid="nav-team-engagement"
        >
          <NavIcon name="team_engagement" /> Zaangażowanie zespołu
        </button>
      )}

      {/* KASA */}
      {(hasPermission('cash_view') || hasPermission('cash_manage') || hasPermission('manager') || hasPermission('settings')) && (
        <button
          className={`nav-btn ${activeTab === "cash" ? "active" : ""}`}
          onClick={() => setActiveTab("cash")}
          data-testid="nav-cash"
        >
          <NavIcon name="cash" /> Kasa
        </button>
      )}

      {/* PLIKI (Drive) — dostępne dla każdego zalogowanego */}
      <button
        className={`nav-btn ${activeTab === "files" ? "active" : ""}`}
        onClick={() => setActiveTab("files")}
        data-testid="nav-files"
      >
        <NavIcon name="files" /> Pliki
      </button>

      {/* INWENTARYZACJA */}
      {(hasPermission('inventory_view') || hasPermission('inventory_count') || hasPermission('inventory_manage') || hasPermission('manager') || hasPermission('settings')) && (
        <button
          className={`nav-btn ${activeTab === "inventory" ? "active" : ""}`}
          onClick={() => setActiveTab("inventory")}
          data-testid="nav-inventory"
        >
          <NavIcon name="inventory" /> Inwentaryzacja
        </button>
      )}

      {/* ZAMKNIĘCIE MIESIĄCA */}
      {(hasPermission('monthly_closing_view') || hasPermission('monthly_closing_edit') || hasPermission('monthly_closing_finalize') || hasPermission('manager') || hasPermission('settings')) && (
        <button
          className={`nav-btn ${activeTab === "monthly_closing" ? "active" : ""}`}
          onClick={() => setActiveTab("monthly_closing")}
          data-testid="nav-monthly-closing"
        >
          <NavIcon name="monthly_closing" /> Zamknięcie miesiąca
        </button>
      )}

      {/* OPINIE RESTAURACJI */}
      {(hasPermission('reviews_view') || hasPermission('reviews_edit') || hasPermission('manager') || hasPermission('settings')) && (
        <button
          className={`nav-btn ${activeTab === "reviews" ? "active" : ""}`}
          onClick={() => setActiveTab("reviews")}
          data-testid="nav-reviews"
        >
          <NavIcon name="reviews" /> Opinie
        </button>
      )}

      {/* REKRUTACJA INTERNETOWA */}
      {(hasPermission('recruitment_apps_view') || hasPermission('manager') || hasPermission('settings')) && (
        <button
          className={`nav-btn ${activeTab === "recruitment_apps" ? "active" : ""}`}
          onClick={() => setActiveTab("recruitment_apps")}
          data-testid="nav-recruitment-apps"
        >
          <NavIcon name="recruitment_apps" /> Rekrutacja internetowa
        </button>
      )}

      {/* SETTINGS */}
      {hasPermission('settings') && (
        <button 
          className={`nav-btn ${activeTab === "settings" ? "active" : ""}`} 
          onClick={() => setActiveTab("settings")}
        >
          <NavIcon name="settings" /> Ustawienia
        </button>
      )}
      </div>
      </aside>

      <SidebarCustomizerModal
        isOpen={showCustomizer}
        onClose={() => setShowCustomizer(false)}
        asideRef={asideRef}
        username={user?.username}
        userId={user?.id}
      />

      <PinGate
        open={showPinGate}
        userId={user?.id}
        onSuccess={() => { setShowPinGate(false); setShowCustomizer(true); }}
        onCancel={() => setShowPinGate(false)}
      />
    </>
  );
};

export default Navbar;

// Zielona pulsująca kropka — informuje o nowym wpisie w module
const UnreadDot = () => (
  <>
    <style>{`
      @keyframes navUnreadPulse {
        0%, 100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7), 0 0 8px rgba(34, 197, 94, 0.6); }
        50%      { box-shadow: 0 0 0 7px rgba(34, 197, 94, 0), 0 0 8px rgba(34, 197, 94, 0.6); }
      }
    `}</style>
    <span
      data-testid="nav-unread-dot"
      aria-label="Nowy wpis"
      title="Nowe wpisy w module"
      style={{
        display: 'inline-block',
        width: 10,
        height: 10,
        borderRadius: '50%',
        marginLeft: 8,
        background: '#22c55e',
        animation: 'navUnreadPulse 1.6s ease-in-out infinite',
        verticalAlign: 'middle',
        flexShrink: 0,
        border: '1px solid rgba(255,255,255,0.55)',
      }}
    />
  </>
);

